import { useEffect, useState } from 'react';
import { Icon, ICON_SIZE } from '../chrome/icons';
import { Spinner } from '../../ui/controls';
import { GROUPS, TOOLS, toolById, type ToolDef, type ToolId } from './catalogue';
import { ToolView, type ImageToolHost } from './ToolViews';
import { onPictureToolRequest, pictureToolRequestFor } from './request';

/*
 * The picture's tools, in the library's Edit with AI slot: a list of names by what they do (Clean
 * up · Change · Take apart · Adjust), and one press opens that tool's OWN screen in the panel —
 * its controls and one button, nothing else (Tarek, 15 Oct, the tools mock: no picture repeated
 * in the panel, no paragraphs). The panel's header takes the tool's name and a Back (`onScreen`).
 * Image to Video is a door: it opens Generate on Video with this picture as the first frame.
 */

const PICTURE_TOOLS: readonly ToolDef[] = TOOLS.filter(tool => tool.group !== 'clip');
const CLIP_TOOLS: readonly ToolDef[] = TOOLS.filter(tool => tool.group === 'clip');

export function PicturePanelSection({ id, host, onScreen }: { id: string; host: ImageToolHost; /** The screen the panel shows, for its header: a tool's name and the way back, or the list. */ onScreen?: (screen: { title: string; back: () => void } | null) => void }) {
  const picture = host.picture(id);
  const [open, setOpen] = useState<ToolId | null>(null);
  // Words the Generate panel's suggestion brought along, for a tool that takes words.
  const [words, setWords] = useState<{ tool: ToolId; text: string } | null>(null);
  // Another picture closes the open tool — unless a tool was just asked for on this one from outside
  // the tab (request.ts, the Generate panel's suggestion): then that tool opens, now or as this mounts.
  useEffect(() => {
    const show = (request: { tool: ToolId; words?: string } | null) => {
      setOpen(request?.tool ?? null);
      setWords(request?.words ? { tool: request.tool, text: request.words } : null);
    };
    show(pictureToolRequestFor(id));
    return onPictureToolRequest((request) => request.id === id && show(request));
  }, [id]);
  useEffect(() => {
    onScreen?.(open && open !== 'animate' ? { title: toolById(open).label, back: () => setOpen(null) } : null);
    return () => onScreen?.(null);
  }, [open, onScreen]);
  // The tool working on this picture says so on its row, open or not.
  const [, refresh] = useState(0);
  useEffect(() => host.onRunsChange?.(() => refresh(n => n + 1)), [host]);
  if (!picture) return null;
  if (open && open !== 'animate') {
    const tool = toolById(open);
    return <div className="tools-panel-screen"><ToolView key={`${id}:${open}`} id={id} tool={tool} host={host} picture={picture} words={words?.tool === open ? words.text : undefined} /></div>;
  }
  const running = host.running(id);
  const local = PICTURE_TOOLS.filter(tool => tool.local);
  // The one-press jobs first in Clean up: what a person most often wants from a photo.
  const ORDER: readonly ToolId[] = ['remove-background', 'erase', 'retouch', 'focus', 'upscale'];
  const rank = (tool: ToolDef) => { const at = ORDER.indexOf(tool.id); return at === -1 ? ORDER.length : at; };
  const groups = picture.video ? [{ id: 'clip', label: 'Clip', tools: CLIP_TOOLS }]
    : GROUPS.map(group => ({ ...group, tools: PICTURE_TOOLS.filter(tool => tool.group === group.id && !tool.local).sort((a, b) => rank(a) - rank(b)) }));
  const row = (tool: ToolDef) => <button key={tool.id} type="button" className="tools-panel-item" onClick={() => { if (tool.id === 'animate') host.toVideo(id); else setOpen(tool.id); }}>
    <Icon name={tool.icon} size={ICON_SIZE.control} /><span>{tool.label}</span>
    {running === tool.label ? <Spinner size={11} /> : <Icon name="chevron" size={ICON_SIZE.indicator} />}
  </button>;
  return <div className="tools-panel">
    {groups.map(group => <div key={group.id} className="tools-panel-group">
      <h4>{group.label}</h4>
      {group.tools.map(row)}
    </div>)}
    {/* Adjust and Blur change the picture live and cost nothing: last, and said so. */}
    {local.length ? <div className="tools-panel-group">
      <h4>Adjust <span className="tools-panel-note">· free, live</span></h4>
      {local.map(row)}
    </div> : null}
  </div>;
}
